// BETA 15.68 — Sound Inspector (BIG EXPANSION)
//
// Per Dylan: "it is missing all guns, and all floor sprite sounds, and all
// other sounds that can be in game. example bungulator. its not a weapon
// but its used in game to attack." Plus: "give me a green check mark box
// beside them that always stays green checked if i ever check it and come
// back. so i can keep track on what i have done."
//
// Catalog jumped from ~30 entries to ~65: every weapon (most fall through
// to revolver_shot.wav fallback), every active item (Bungulator, Karen
// Drones, Iranian Bird, T-Bag, etc.), every floor-sprite item (SHIT,
// Whoopie, Banana — flagged MISSING since they have no sound), all UI
// sounds (CRT boot, click, hover, etc.), the new chest free/paid split,
// and the new regular shrine activation.
//
// Each row now has a green ✓ checkbox that PERSISTS in localStorage so
// Dylan can mark sounds as "done sourcing" and they stay checked across
// sessions.
//
// Each ▶ calls the EXACT play function from gameSfx.ts / audio.ts so
// volume/spatial/throttle match the live game.

import React, { useEffect, useMemo, useState } from 'react';
import {
  // Weapons
  playRevolverShot,
  playRevolverReload,
  playRevolverEmpty,
  playRifleShot,
  playRifleReload,
  playSpatulaSlash,
  playBasketballBounce,
  // BETA 15.74 — new weapon-specific samples
  playBlunderbussFire,
  playVikingAxeThrow,
  playVoogooRagerThrow,
  playFireballWandFire,
  playTerminatorFire,
  playUltraBeamFire,
  playKittensSpawn,
  playKittensAttack,
  // BETA 15.75 — Poop Sling fire + hit
  playPoopSlingFire,
  playPoopSlingHit,
  // BETA 15.76 — Squirter fire (no reload — continuous stream)
  playSquirterFire,
  playSquirterReload,
  // BETA 15.77 — Frozen Darts, Drac's Fang (melee swing), Shitty Cannon,
  // Ex Girlfriend (5 overlapping shots)
  playFrozenDartsFire,
  playDracsFangSwing,
  playShittyCannonFire,
  playExGirlfriendFire,
  // BETA 15.78 — Hurray fire + Bungulator punch
  playHurrayFire,
  playBungulatorPunch,
  // Enemies
  playEnemyHit,
  playEnemyDeath,
  playEnemyWallJump,
  // Items
  playAnusRocket,
  playDiarrheaGas,
  playGasEffect,
  playTBagSummon,
  // Pickups (procedural)
  playXPPickup,
  playGoldPickup,
  playHealthPickup,
  // Pickups + warning (now sample-backed in 15.68)
  playLevelUp,
  playLowHealthWarning,
  // Chest open (now split free/paid in 15.68)
  playChestOpenFree,
  playChestOpenPaid,
  // Procedural still
  playBarrelBreak,
  // Player
  playPlayerHit,
  playPlayerDeath,
  // World / shrines
  playShrineActivate,
  startTomeChanneling,
  playTomeComplete,
  // UI
  playCardHover,
  // Listener pinning (BETA 15.66)
  updateAudioListener,
} from '../game/gameSfx';
// BETA 15.68 — UI sounds defined in audio.ts (separate from gameSfx).
import {
  playCRTBoot,
  playUIClick,
  playUIHover,
  playDeploy,
  playGoopCollect,
  playPortalActivate,
  playVictory,
  playFootstep,
  isMusicPlaying,
  startMusic,
  stopMusic,
  // BETA 15.81 — Dylan's BGM tracks (loop continuously, persist across
  // phase changes). Inspector mutes them while open so the user can hear
  // sample sounds clearly.
  isBgmPlaying,
  startBgm,
  stopBgm,
  // BETA 15.85 — playlist track jump (play a specific track from the playlist).
  playBgmTrack,
} from '../game/audio';
// BETA 15.66 — Pause the game + stop music while inspector is open so the user
// can hear each sound individually at the same loudness as in-game.
import { setLabCalibratorOpen } from '../game/weaponLab';
// BETA 15.70 — Suno Prompt Library (PSBOT RUN spec). When an inspector row
// maps to a weapon with prompts, expanding shows 3 copy-able Suno prompts
// per slot (fire/reload/hit/etc.) so Dylan can copy → paste in Suno → make.
import {
  type SoundSlot, type SoundPrompt,
  getSoundPrompts, getSlotsForItem, hasPromptsForItem, formatPromptForCopy,
} from '../game/soundPrompts';

interface Props {
  onClose: () => void;
}

type Source = 'user-file' | 'procedural' | 'shared' | 'dead' | 'missing';

type Category =
  | 'weapons'
  | 'weapon-fallback'
  | 'enemies'
  | 'items-active'
  | 'items-floor'
  | 'items-effect'
  | 'pickups'
  | 'player'
  | 'world'
  | 'ui'
  | 'dead';

interface SoundEntry {
  /** Stable ID for localStorage checkbox persistence. Don't rename. */
  id: string;
  category: Category;
  label: string;
  source: Source;
  /** Path under /sounds/ — only set for sample-backed entries */
  file?: string;
  /** For procedural — describe what the synth does so user knows what to expect */
  description?: string;
  /** If shared, list the OTHER places that use the same file */
  sharedWith?: string[];
  /** If this sound is a fallback (e.g. revolver_shot for all guns) — note it */
  fallbackFor?: string[];
  /** The actual play call. May be a wrapper for sounds that take args. */
  play: () => void;
  /** The function name from gameSfx.ts — shown as muted trace text */
  playFnName: string;
}

// ─────────────────────────────────────────────────────────────────────────
// CATALOG — single source of truth (BETA 15.68 expansion)
// ─────────────────────────────────────────────────────────────────────────

const CATALOG: SoundEntry[] = [
  // ─── WEAPONS — UNIQUE SOUNDS ──────────────────────────────────────
  {
    id: 'wpn_revolver',
    category: 'weapons',
    label: 'Revolver fire (The Six Shooter)',
    source: 'shared',
    file: '/sounds/revolver_shot.wav',
    fallbackFor: [
      'EVERY OTHER projectile gun (rifle, spatula, basketball excepted)',
      'See weapon-fallback section below',
    ],
    play: () => playRevolverShot(),
    playFnName: 'playRevolverShot',
  },
  {
    id: 'wpn_revolver_reload',
    category: 'weapons',
    label: 'Revolver reload (the_six_shooter ONLY)',
    source: 'user-file',
    file: '/sounds/revolver_reload.mp3',
    description: 'Per Dylan (15.69): only fires when the revolver weapon reloads. Other weapons reload silently until Dylan provides a sample for that specific weapon.',
    play: () => playRevolverReload(),
    playFnName: 'playRevolverReload',
  },
  {
    id: 'wpn_revolver_empty',
    category: 'weapons',
    label: 'Revolver dry-fire (empty chamber)',
    source: 'user-file',
    file: '/sounds/revolver_empty.mp3',
    play: () => playRevolverEmpty(),
    playFnName: 'playRevolverEmpty',
  },
  {
    id: 'wpn_rifle',
    category: 'weapons',
    label: 'Peanut Rifle fire (sniper)',
    source: 'user-file',
    file: '/sounds/sniper_shot.wav',
    play: () => playRifleShot(),
    playFnName: 'playRifleShot',
  },
  {
    id: 'wpn_rifle_reload',
    category: 'weapons',
    label: 'Peanut Rifle reload (bolt action)',
    source: 'user-file',
    file: '/sounds/rifle_reload.mp3',
    play: () => playRifleReload(),
    playFnName: 'playRifleReload',
  },
  {
    id: 'wpn_spatula',
    category: 'weapons',
    label: 'Spatula slash',
    source: 'user-file',
    file: '/sounds/spatchula-sound-1.wav',
    play: () => playSpatulaSlash(),
    playFnName: 'playSpatulaSlash',
  },
  {
    id: 'wpn_basketball',
    category: 'weapons',
    label: 'Tactical Basketball bounce',
    source: 'user-file',
    file: '/sounds/basketball_bounce.wav',
    play: () => playBasketballBounce(),
    playFnName: 'playBasketballBounce',
  },

  // ─── WEAPONS — FALLBACK GROUP (revolver_shot.wav for all) ─────────
  // Each of these 17 weapons currently plays revolver_shot.wav on fire.
  // Replacing any of these with a unique sound = direct upgrade.
  {
    id: 'wpn_rpk',
    category: 'weapon-fallback',
    label: 'RPK Machine Gun fire',
    source: 'shared',
    file: '/sounds/revolver_shot.wav',
    sharedWith: ['Revolver + every other fallback gun'],
    play: () => playRevolverShot(),
    playFnName: 'playRevolverShot (fallback)',
  },
  {
    id: 'wpn_fireball_wand',
    category: 'weapons',
    label: 'Fireball Wand fire',
    source: 'user-file',
    file: '/sounds/fireball_wand_fire.wav',
    play: () => playFireballWandFire(),
    playFnName: 'playFireballWandFire',
  },
  {
    id: 'wpn_viking_axe',
    category: 'weapons',
    label: 'Viking Axe throw',
    source: 'user-file',
    file: '/sounds/viking_axe_throw.wav',
    play: () => playVikingAxeThrow(),
    playFnName: 'playVikingAxeThrow',
  },
  {
    id: 'wpn_voogoo_rager',
    category: 'weapons',
    label: 'Voogoo Rager Toss',
    source: 'user-file',
    file: '/sounds/voogoo_rager_toss_throw.wav',
    description: 'Throw sound only — Dylan said this weapon has just 2 sounds (throw + impact). Impact sound not yet provided.',
    play: () => playVoogooRagerThrow(),
    playFnName: 'playVoogooRagerThrow',
  },
  {
    id: 'wpn_blunderbuss',
    category: 'weapons',
    label: 'Blunderbuss fire',
    source: 'user-file',
    file: '/sounds/blunderbuss_fire.wav',
    play: () => playBlunderbussFire(),
    playFnName: 'playBlunderbussFire',
  },
  {
    id: 'wpn_ultra_beam',
    category: 'weapons',
    label: 'Ultra Beam fire (15.74 charge-up rework)',
    source: 'user-file',
    file: '/sounds/wpn_ultra_beam_fire.wav',
    description: 'Audio has built-in 0.737s charge-up build → BLAM at end. Plays at trigger; projectile spawns at the BLAM moment so audio + visual align. playbackRate scales with attack-speed cooldown to keep BLAM-on-spawn at any fire rate.',
    play: () => { playUltraBeamFire(1.0); },
    playFnName: 'playUltraBeamFire',
  },
  {
    id: 'wpn_terminator',
    category: 'weapons',
    label: 'The Terminator fire (boomerang)',
    source: 'user-file',
    file: '/sounds/wpn_the_terminator_fire.wav',
    play: () => playTerminatorFire(),
    playFnName: 'playTerminatorFire',
  },
  {
    id: 'wpn_kittens',
    category: 'weapons',
    label: 'Ugly Kittens — spawn meow (on fire)',
    source: 'user-file',
    file: '/sounds/kittens_spawn.wav',
    description: 'Plays when a kitten projectile is launched/placed on the ground. Paired with kittens_attack.wav which plays on enemy contact (separate entry).',
    play: () => playKittensSpawn(),
    playFnName: 'playKittensSpawn',
  },
  {
    id: 'wpn_kittens_attack',
    category: 'weapons',
    label: 'Ugly Kittens — cat fight (on hit)',
    source: 'user-file',
    file: '/sounds/kittens_attack.wav',
    description: 'Plays when a kitten contacts an enemy (fight sound). Spatial. Throttled 0.15s so multiple simultaneous kitten hits don\'t overlap-stack.',
    play: () => playKittensAttack(0, 0),
    playFnName: 'playKittensAttack',
  },
  {
    id: 'wpn_karen_drones',
    category: 'weapon-fallback',
    label: 'Karen Drones (orbital)',
    source: 'shared',
    file: '/sounds/revolver_shot.wav',
    sharedWith: ['Revolver fallback — orbital contact damage uses zombie_grunt on hit'],
    play: () => playRevolverShot(),
    playFnName: 'playRevolverShot (fallback)',
  },
  {
    id: 'wpn_poop_sling',
    category: 'weapons',
    label: 'Poop Sling — fire (shoot)',
    source: 'user-file',
    file: '/sounds/poop_sling_fire.mp3',
    play: () => playPoopSlingFire(),
    playFnName: 'playPoopSlingFire',
  },
  {
    id: 'wpn_poop_sling_hit',
    category: 'weapons',
    label: 'Poop Sling — hit (impact on enemy)',
    source: 'user-file',
    file: '/sounds/poop_sling_hit.mp3',
    description: 'Spatial sound. Throttled 0.1s so piercing shots through multiple enemies don\'t overlap-stack.',
    play: () => playPoopSlingHit(0, 0),
    playFnName: 'playPoopSlingHit',
  },
  {
    id: 'wpn_squirter',
    category: 'weapons',
    label: 'Squirter — fire (continuous water stream)',
    source: 'user-file',
    file: '/sounds/squirter_fire.mp3',
    description: 'Plays every shot. Throttle 0.06s matches the weapon\'s minReloadTime floor so high-attack-speed firing stays clean.',
    play: () => playSquirterFire(),
    playFnName: 'playSquirterFire',
  },
  {
    id: 'wpn_squirter_reload_unused',
    category: 'dead',
    label: 'Squirter reload (file present, NOT wired)',
    source: 'dead',
    file: '/sounds/squirter_reload.mp3',
    description: 'Per Dylan (15.76): Squirter has no real magazine — fires every 0.12s with no pause. Wiring a reload sound would play it 8/sec which is unusable. File staged but no game event triggers it. Use this entry to audition the file.',
    play: () => playSquirterReload(),
    playFnName: 'playSquirterReload (staged, no caller)',
  },
  {
    id: 'wpn_frozen_darts',
    category: 'weapons',
    label: 'Frozen Darts — fire',
    source: 'user-file',
    file: '/sounds/frozen_darts_fire.wav',
    play: () => playFrozenDartsFire(),
    playFnName: 'playFrozenDartsFire',
  },
  {
    id: 'wpn_dracs_fang',
    category: 'weapons',
    label: "Drac's Fang — swing (melee arc)",
    source: 'user-file',
    file: '/sounds/dracs_fang_swing.mp3',
    description: 'Plays each time the fang completes an arc swing. Detected via arcSlashes spriteUrl matching (same pattern as spatula).',
    play: () => playDracsFangSwing(),
    playFnName: 'playDracsFangSwing',
  },
  {
    id: 'wpn_shitty_cannon',
    category: 'weapons',
    label: 'Shitty Cannon — fire',
    source: 'user-file',
    file: '/sounds/shitty_cannon_fire.wav',
    play: () => playShittyCannonFire(),
    playFnName: 'playShittyCannonFire',
  },
  {
    id: 'wpn_ex_girlfriend',
    category: 'weapons',
    label: 'The Ex Girlfriend — fire (5 sequential overlapping shots)',
    source: 'user-file',
    file: '/sounds/ex_girlfriend_fire.mp3',
    description: 'Per Dylan: gun fires 1,2,3,4,5 quickly back-to-back; sounds overlap (don\'t cut each other off). Implementation: weapon\'s burstDelay 0→0.08s so projectiles fire sequentially over 0.4s; each shot triggers a new BufferSource so plays naturally overlap.',
    play: () => playExGirlfriendFire(),
    playFnName: 'playExGirlfriendFire',
  },
  {
    id: 'wpn_hurray',
    category: 'weapons',
    label: 'Hurray — fire',
    source: 'user-file',
    file: '/sounds/hurray_fire.wav',
    play: () => playHurrayFire(),
    playFnName: 'playHurrayFire',
  },
  {
    id: 'wpn_chad_aura',
    category: 'weapon-fallback',
    label: 'Chad Aura (silent — by design)',
    source: 'missing',
    description: 'Aura weapon has no firing sound. Not a bug — auras are intentionally silent. Could add a subtle pulse hum.',
    play: () => { /* nothing — no sound currently */ },
    playFnName: '(none — silent aura)',
  },

  // ─── ENEMIES ──────────────────────────────────────────────────────
  {
    id: 'enemy_hit',
    category: 'enemies',
    label: 'Enemy HIT (any enemy taking damage)',
    source: 'shared',
    file: '/sounds/zombie_grunt.wav',
    sharedWith: [
      'Every enemy type (zombie, killa, big boys, all bosses)',
      'Bungulator punches, Karen drones contact, Iranian Bird, T-Bag attacks (all use playEnemyHit)',
      'Plays alongside hitmarker.mp3 overlay',
    ],
    play: () => playEnemyHit(0, 0),
    playFnName: 'playEnemyHit',
  },
  {
    id: 'enemy_death',
    category: 'enemies',
    label: 'Enemy DEATH (any enemy killed)',
    source: 'shared',
    file: '/sounds/zombie_death.wav',
    sharedWith: ['Every enemy type — capped at 4 concurrent to prevent distortion'],
    play: () => playEnemyDeath(0, 0),
    playFnName: 'playEnemyDeath',
  },
  {
    id: 'enemy_wall_jump',
    category: 'enemies',
    label: 'Wall-hop jump (15.53+)',
    source: 'user-file',
    file: '/sounds/enemy_wall_jump.wav',
    play: () => playEnemyWallJump(0, 0),
    playFnName: 'playEnemyWallJump',
  },
  {
    id: 'enemy_spawn_dead',
    category: 'enemies',
    label: 'Enemy spawn rumble (REFERENCED but never called)',
    source: 'dead',
    description: 'playEnemySpawn() commented in gameSfx.ts but no code path calls it. Dead reference.',
    play: () => { /* nothing */ },
    playFnName: '(none — reference only)',
  },

  // ─── ITEMS — ACTIVE ATTACKERS (Bungulator etc.) ───────────────────
  {
    id: 'item_bungulator',
    category: 'items-active',
    label: 'Bungulator (R01) — punch impact',
    source: 'user-file',
    file: '/sounds/bungulator_punch.wav',
    description: 'Plays each time a Bungulator minion punches an enemy. Spatial. Throttled 0.12s so multiple stacked Bungulators (up to 6+ from item stacks + N06 perk grants) don\'t overlap into noise.',
    play: () => playBungulatorPunch(0, 0),
    playFnName: 'playBungulatorPunch',
  },
  {
    id: 'item_iranian_bird',
    category: 'items-active',
    label: 'Iranian Bird (R17) — peck attack',
    source: 'shared',
    file: '/sounds/zombie_grunt.wav',
    sharedWith: ['Every enemy hit'],
    play: () => playEnemyHit(0, 0),
    playFnName: 'playEnemyHit (shared)',
  },
  {
    id: 'item_t_bag_summon',
    category: 'items-active',
    label: 'R16 Nutty T-Bag — summon',
    source: 'user-file',
    file: '/sounds/t_bag.wav',
    play: () => playTBagSummon(),
    playFnName: 'playTBagSummon',
  },
  {
    id: 'item_t_bag_attack',
    category: 'items-active',
    label: 'R16 Nutty T-Bag — attack hit',
    source: 'shared',
    file: '/sounds/zombie_grunt.wav',
    sharedWith: ['Every enemy hit'],
    play: () => playEnemyHit(0, 0),
    playFnName: 'playEnemyHit (shared)',
  },

  // ─── ITEMS — FLOOR/GROUND SPRITES ─────────────────────────────────
  // These items DROP SPRITES on the ground but currently have NO SOUND on
  // spawn or step-on. Strong candidates for new sound assets.
  {
    id: 'item_shit_spawn',
    category: 'items-floor',
    label: 'SHIT (R09) — puddle spawns',
    source: 'missing',
    description: 'Drops poop pile every 1.5/sqrt(stacks)s. NO sound on spawn or step-on. Sprite: /items/item_poop_deck_ground.png → /items/item_shit_puddle.png after step.',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },
  {
    id: 'item_whoopie_spawn',
    category: 'items-floor',
    label: 'Whoopie Cushion of Doom (R13) — drop',
    source: 'missing',
    description: 'Auto-drops every 6s. NO sound on spawn. On contact: only VFX particles. Sprite: /items/item_whoopie_cushion_of_doom_ground.png',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },
  {
    id: 'item_whoopie_step',
    category: 'items-floor',
    label: 'Whoopie Cushion — first step (slow trigger)',
    source: 'missing',
    description: 'Enemy first contact triggers slow + brown VFX. NO audio. Begs for fart sound.',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },
  {
    id: 'item_banana_spawn',
    category: 'items-floor',
    label: "Asmon's Desk Banana (R14) — drop",
    source: 'missing',
    description: 'Auto-drops every 8s. NO sound on spawn. Sprite: /items/item_asmons_desk_banana_ground.png',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },
  {
    id: 'item_banana_explode',
    category: 'items-floor',
    label: 'Asmons Banana — first step (35dmg burst)',
    source: 'missing',
    description: 'Enemy contact → 35dmg + yellow/brown explosion VFX. NO audio. Begs for slip + bonk sound.',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },

  // ─── ITEMS — EFFECT (gas, rocket, etc.) ───────────────────────────
  {
    id: 'item_anus_rocket',
    category: 'items-effect',
    label: 'R08 Anus Rocket — launch',
    source: 'user-file',
    file: '/sounds/rocket_sound.wav',
    play: () => playAnusRocket(0, 0),
    playFnName: 'playAnusRocket',
  },
  {
    id: 'item_diarrhea_gas',
    category: 'items-effect',
    label: 'R11 Diarrhea Gas (fart + gas combo)',
    source: 'shared',
    file: '/sounds/fart.wav + /sounds/gas.wav',
    sharedWith: ['gas.wav also used by R12 Gas Mask'],
    play: () => playDiarrheaGas(),
    playFnName: 'playDiarrheaGas',
  },
  {
    id: 'item_gas_mask',
    category: 'items-effect',
    label: 'R12 Gas Mask effect (gas only)',
    source: 'shared',
    file: '/sounds/gas.wav',
    sharedWith: ['Same file as R11 Diarrhea Gas (just no fart layered on top)'],
    play: () => playGasEffect(),
    playFnName: 'playGasEffect',
  },

  // ─── PICKUPS / GROUND PICKUPS ─────────────────────────────────────
  {
    id: 'pickup_xp',
    category: 'pickups',
    label: 'XP gem pickup',
    source: 'procedural',
    description: 'Sine sweep 1200 → 2400 Hz over 80 ms (sparkle ding)',
    play: () => playXPPickup(),
    playFnName: 'playXPPickup',
  },
  {
    id: 'pickup_gold',
    category: 'pickups',
    label: 'Gold coin pickup',
    source: 'procedural',
    description: '3.2 kHz coin clink + 4.8 kHz harmonic',
    play: () => playGoldPickup(),
    playFnName: 'playGoldPickup',
  },
  {
    id: 'pickup_health',
    category: 'pickups',
    label: 'Health pickup',
    source: 'procedural',
    description: 'Warm sine 440 → 880 Hz + 1.32 kHz shimmer',
    play: () => playHealthPickup(),
    playFnName: 'playHealthPickup',
  },
  {
    id: 'pickup_goop',
    category: 'pickups',
    label: 'Goop puddle collect',
    source: 'procedural',
    description: 'Gear crunch + sparkle (audio.ts)',
    play: () => playGoopCollect(),
    playFnName: 'playGoopCollect',
  },
  {
    id: 'pickup_levelup',
    category: 'pickups',
    label: 'Level-up fanfare',
    source: 'user-file',
    file: '/sounds/level_up.wav',
    play: () => playLevelUp(),
    playFnName: 'playLevelUp',
  },
  {
    id: 'pickup_chest_free',
    category: 'pickups',
    label: 'FREE chest open (gold chest, ultra boss)',
    source: 'user-file',
    file: '/sounds/chest_free_open.wav',
    play: () => playChestOpenFree(),
    playFnName: 'playChestOpenFree',
  },
  {
    id: 'pickup_chest_paid',
    category: 'pickups',
    label: 'PAID chest open (goop chest — costs gold)',
    source: 'user-file',
    file: '/sounds/chest_paid_open.wav',
    play: () => playChestOpenPaid(),
    playFnName: 'playChestOpenPaid',
  },
  {
    id: 'pickup_barrel',
    category: 'pickups',
    label: 'Barrel break',
    source: 'procedural',
    description: 'High wood crack (noise) + low thud + mid rattle',
    play: () => playBarrelBreak(),
    playFnName: 'playBarrelBreak',
  },
  {
    id: 'pickup_item_pickup',
    category: 'pickups',
    label: 'Picking up an item from ground',
    source: 'missing',
    description: 'No sound currently fires when player walks over an item. Strong candidate for a "swoosh" or "tink" sample.',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },

  // ─── PLAYER ───────────────────────────────────────────────────────
  {
    id: 'player_hit',
    category: 'player',
    label: 'Player takes damage',
    source: 'procedural',
    description: '400 Hz bandpass noise burst + 80 Hz low rumble',
    play: () => playPlayerHit(),
    playFnName: 'playPlayerHit',
  },
  {
    id: 'player_death',
    category: 'player',
    label: 'Player death',
    source: 'procedural',
    description: 'Descending square chromatic 440 → 330 Hz + low sawtooth drone',
    play: () => playPlayerDeath(),
    playFnName: 'playPlayerDeath',
  },
  {
    id: 'player_low_hp',
    category: 'player',
    label: 'Low-HP heartbeat warning',
    source: 'user-file',
    file: '/sounds/low_hp_heartbeat.wav',
    play: () => playLowHealthWarning(),
    playFnName: 'playLowHealthWarning',
  },
  {
    id: 'player_footstep',
    category: 'player',
    label: 'Player footstep / dash',
    source: 'procedural',
    description: '60±10 Hz sine + click, rate-limited 0.25s',
    play: () => playFootstep(),
    playFnName: 'playFootstep',
  },
  {
    id: 'player_dash',
    category: 'player',
    label: 'Player dash / dodge ability',
    source: 'missing',
    description: 'No distinct dash sound. Currently uses footstep.',
    play: () => { /* nothing */ },
    playFnName: '(none — needs sound)',
  },

  // ─── WORLD / SHRINES ──────────────────────────────────────────────
  {
    id: 'world_shrine_regular',
    category: 'world',
    label: 'Regular shrine — full sound (step-on through completion, 15.70+)',
    source: 'user-file',
    file: '/sounds/shrine_activate.wav',
    description: 'Plays from the moment you step on a shrine. Continues through activation, buff selection UI, click, and after-click. Stops only if you step off before activation completes (then restarts on next step-on). Replaced the old synth channeling hum (startTomeChanneling) entirely.',
    play: () => playShrineActivate(),
    playFnName: 'playShrineActivate',
  },
  {
    id: 'world_tome_channel',
    category: 'dead',
    label: 'Tome shrine — channeling hum (REPLACED in 15.70)',
    source: 'dead',
    description: 'Old synth orbish hum. As of 15.70 the shrine experience uses Dylan-provided shrine_activate.wav from step-on. This synth function still exists in gameSfx.ts but no code path calls it anymore.',
    play: () => {
      const stop = startTomeChanneling();
      window.setTimeout(stop, 3000);
    },
    playFnName: 'startTomeChanneling (orphaned)',
  },
  {
    id: 'world_tome_complete',
    category: 'dead',
    label: 'Tome shrine — activation complete (REPLACED in 15.70)',
    source: 'dead',
    description: 'Old synth rising sweep + chord. Replaced by shrine_activate.wav playing through naturally.',
    play: () => playTomeComplete(),
    playFnName: 'playTomeComplete (orphaned)',
  },
  {
    id: 'world_portal',
    category: 'world',
    label: 'Portal activate / room transition',
    source: 'procedural',
    description: 'Dual sweep 100→800 + 200→1600 Hz',
    play: () => playPortalActivate(),
    playFnName: 'playPortalActivate',
  },
  {
    id: 'world_victory',
    category: 'world',
    label: 'Victory fanfare',
    source: 'procedural',
    description: '4-note fanfare C5-E5-G5-C6',
    play: () => playVictory(),
    playFnName: 'playVictory',
  },
  // BETA 15.85 — BGM playlist. Plays in this order, auto-advances to the
  // next track when one ends, loops back after the last. ▶ on any entry
  // jumps the playlist to that track and plays from there.
  {
    id: 'world_bgm_track1',
    category: 'world',
    label: 'BGM — "I Play Video Games" (track 1, plays first)',
    source: 'user-file',
    file: '/sounds/bgm_i_play_video_games.mp3',
    description: 'First lobby track. Auto-advances to "Sorry Boss" when it ends.',
    play: () => { void playBgmTrack('/sounds/bgm_i_play_video_games.mp3'); window.setTimeout(() => stopBgm(), 6000); },
    playFnName: 'playBgmTrack (6s preview)',
  },
  {
    id: 'world_bgm_track2',
    category: 'world',
    label: 'BGM — "Sorry Boss" (track 2)',
    source: 'user-file',
    file: '/sounds/bgm_bungulators_mute_mom.mp3',
    description: 'Plays after "I Play Video Games". Auto-advances to "The Anthem" when it ends.',
    play: () => { void playBgmTrack('/sounds/bgm_bungulators_mute_mom.mp3'); window.setTimeout(() => stopBgm(), 6000); },
    playFnName: 'playBgmTrack (6s preview)',
  },
  {
    id: 'world_bgm_track3',
    category: 'world',
    label: 'BGM — "The Anthem" (track 3)',
    source: 'user-file',
    file: '/sounds/bgm_the_anthem.mp3',
    description: 'Plays after "Sorry Boss". Auto-advances to "My Friends Are Trash" when it ends.',
    play: () => { void playBgmTrack('/sounds/bgm_the_anthem.mp3'); window.setTimeout(() => stopBgm(), 6000); },
    playFnName: 'playBgmTrack (6s preview)',
  },
  {
    id: 'world_bgm_track4',
    category: 'world',
    label: 'BGM — "My Friends Are Trash" (track 4, last in cycle)',
    source: 'user-file',
    file: '/sounds/bgm_my_friends_are_trash.mp3',
    description: 'Plays after "The Anthem". After this track, playlist loops back to "I Play Video Games".',
    play: () => { void playBgmTrack('/sounds/bgm_my_friends_are_trash.mp3'); window.setTimeout(() => stopBgm(), 6000); },
    playFnName: 'playBgmTrack (6s preview)',
  },

  // ─── UI ───────────────────────────────────────────────────────────
  {
    id: 'ui_card_hover',
    category: 'ui',
    label: 'Level-up card hover',
    source: 'procedural',
    description: 'Soft sine 700 → 1000 Hz blip',
    play: () => playCardHover(),
    playFnName: 'playCardHover',
  },
  {
    id: 'ui_click',
    category: 'ui',
    label: 'UI button click',
    source: 'procedural',
    description: '1200 Hz square, 60 ms decay',
    play: () => playUIClick(),
    playFnName: 'playUIClick',
  },
  {
    id: 'ui_hover',
    category: 'ui',
    label: 'UI hover',
    source: 'procedural',
    description: '800 Hz sine, 40 ms decay',
    play: () => playUIHover(),
    playFnName: 'playUIHover',
  },
  {
    id: 'ui_crt_boot',
    category: 'ui',
    label: 'CRT boot (title screen / start)',
    source: 'procedural',
    description: '3 ascending beeps 440 → 660 → 880 Hz',
    play: () => playCRTBoot(),
    playFnName: 'playCRTBoot',
  },
  {
    id: 'ui_deploy',
    category: 'ui',
    label: 'Deploy (game start / round begin)',
    source: 'procedural',
    description: '2000→100 Hz sawtooth sweep + LPF',
    play: () => playDeploy(),
    playFnName: 'playDeploy',
  },

  // ─── DEAD / UNUSED ────────────────────────────────────────────────
  {
    id: 'dead_zombie_hit',
    category: 'dead',
    label: 'zombie_hit.wav (file exists, never referenced)',
    source: 'dead',
    file: '/sounds/zombie_hit.wav',
    description: 'Loaded into bufferCache but no play function references it. Safe to delete from /sounds/ or assign to an event.',
    play: () => {
      try {
        const a = new Audio('/sounds/zombie_hit.wav');
        a.volume = 0.6;
        void a.play();
      } catch { /* ignore */ }
    },
    playFnName: '(no play function — direct file audition)',
  },
];

// ─────────────────────────────────────────────────────────────────────────
// SUNO ID MAP — connects inspector entry IDs to soundPrompts.ts itemIds.
// When an inspector row's id appears here, expanding it shows the 🎵 Suno
// Prompt Library panel for that weapon (all slots × 3 variants).
// BETA 15.70 — PSBOT RUN spec.
// ─────────────────────────────────────────────────────────────────────────
const SUNO_ID_BY_INSPECTOR_ID: Record<string, string> = {
  // Unique-sound weapons section
  'wpn_revolver':         'revolver',
  'wpn_revolver_reload':  'revolver',
  'wpn_revolver_empty':   'revolver',
  'wpn_rifle':            'peanut_rifle',
  'wpn_rifle_reload':     'peanut_rifle',
  'wpn_spatula':          'spatula_slash',
  'wpn_basketball':       'tactical_basketball',
  // Fallback group (currently all share revolver_shot.wav — Suno prompts let Dylan replace each one)
  'wpn_rpk':              'rpk_machine_gun',
  'wpn_fireball_wand':    'fireball_wand',
  'wpn_viking_axe':       'viking_axe',
  'wpn_voogoo_rager':     'voogoo_rager_toss',
  'wpn_blunderbuss':      'blunderbuss',
  'wpn_ultra_beam':       'wpn_ultra_beam',
  'wpn_terminator':       'wpn_the_terminator',
  'wpn_kittens':          'wpn_ugly_kittens',
  'wpn_karen_drones':     'wpn_karen_drones',
  'wpn_poop_sling':       'wpn_poop_sling',
  'wpn_squirter':         'wpn_squirter',
  'wpn_frozen_darts':     'wpn_frozen_darts',
  'wpn_dracs_fang':       'wpn_dracs_fang',
  'wpn_shitty_cannon':    'wpn_shitty_cannon',
  'wpn_ex_girlfriend':    'wpn_ex_girlfriend',
  'wpn_hurray':           'wpn_hurray',
  'wpn_chad_aura':        'chad_aura',
};

function getSunoIdFor(inspectorId: string): string | null {
  const sunoId = SUNO_ID_BY_INSPECTOR_ID[inspectorId];
  if (!sunoId) return null;
  return hasPromptsForItem(sunoId) ? sunoId : null;
}

const SLOT_LABELS: Record<SoundSlot, string> = {
  weapon_fire:     'WEAPON FIRE',
  weapon_reload:   'WEAPON RELOAD',
  weapon_dryfire:  'WEAPON DRY-FIRE',
  weapon_hit:      'WEAPON HIT (impact)',
  weapon_swing:    'WEAPON SWING (melee whoosh)',
  weapon_throw:    'WEAPON THROW (launch)',
  weapon_loop_hum: 'WEAPON LOOP / HUM',
  weapon_charge:   'WEAPON CHARGE (build-up)',
};

// ─────────────────────────────────────────────────────────────────────────
// CHECKMARK PERSISTENCE — Dylan asked for "green check mark box beside them
// that always stays green checked if i ever check it and come back. so i
// can keep track on what i have done."
// ─────────────────────────────────────────────────────────────────────────

const CHECKED_KEY = 'sound_inspector_checked_v1';

function loadChecked(): Set<string> {
  try {
    const raw = localStorage.getItem(CHECKED_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}

function saveChecked(s: Set<string>): void {
  try { localStorage.setItem(CHECKED_KEY, JSON.stringify(Array.from(s))); }
  catch { /* quota */ }
}

// ─────────────────────────────────────────────────────────────────────────
// UI
// ─────────────────────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<Category, string> = {
  weapons: 'WEAPONS — UNIQUE SOUNDS',
  'weapon-fallback': 'WEAPONS — FALLBACK GROUP (all share revolver_shot.wav)',
  enemies: 'ENEMIES',
  'items-active': 'ITEMS — ACTIVE ATTACKERS',
  'items-floor': 'ITEMS — FLOOR / GROUND SPRITES',
  'items-effect': 'ITEMS — EFFECTS',
  pickups: 'PICKUPS / WORLD INTERACT',
  player: 'PLAYER',
  world: 'WORLD / SHRINES',
  ui: 'UI',
  dead: 'DEAD / UNUSED',
};

const CATEGORY_ORDER: Category[] = [
  'weapons',
  'weapon-fallback',
  'enemies',
  'items-active',
  'items-floor',
  'items-effect',
  'pickups',
  'player',
  'world',
  'ui',
  'dead',
];

type Filter = 'all' | 'user-file' | 'procedural' | 'shared' | 'missing' | 'dead' | 'unchecked';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'ALL' },
  { id: 'unchecked', label: '◻ UNCHECKED ONLY' },
  { id: 'user-file', label: '🟢 USER FILE' },
  { id: 'procedural', label: '🤖 PROCEDURAL' },
  { id: 'shared', label: '🟠 SHARED' },
  { id: 'missing', label: '🔴 MISSING' },
  { id: 'dead', label: '⚫ DEAD' },
];

// BETA 15.73 — Category tabs (top row), per Dylan: "i need tabs so i can
// filter just weapons and such. all the things so i can be more organized
// on what im looking for." Categories collapse the ~11 internal sub-
// categories into 9 tabs; ALL shows everything; the source-type pills
// (filter row below) remain orthogonal so user can stack e.g. "WEAPONS"
// tab + "🔴 MISSING" pill to focus on weapons that need new sounds.
type CategoryFilter = 'all' | 'weapons' | 'enemies' | 'items' | 'pickups' | 'player' | 'world' | 'ui' | 'dead';

const CATEGORY_TABS: Array<{ id: CategoryFilter; label: string }> = [
  { id: 'all',     label: 'ALL' },
  { id: 'weapons', label: '🔫 WEAPONS' },
  { id: 'enemies', label: '🧟 ENEMIES' },
  { id: 'items',   label: '🎁 ITEMS' },
  { id: 'pickups', label: '💎 PICKUPS' },
  { id: 'player',  label: '🥜 PLAYER' },
  { id: 'world',   label: '⛩ WORLD' },
  { id: 'ui',      label: '🖱 UI' },
  { id: 'dead',    label: '⚫ DEAD' },
];

/** Map fine-grained CATEGORY (data) onto the user-facing tab. */
function categoryToTab(cat: Category): CategoryFilter {
  switch (cat) {
    case 'weapons':         return 'weapons';
    case 'weapon-fallback': return 'weapons';
    case 'enemies':         return 'enemies';
    case 'items-active':    return 'items';
    case 'items-floor':     return 'items';
    case 'items-effect':    return 'items';
    case 'pickups':         return 'pickups';
    case 'player':          return 'player';
    case 'world':           return 'world';
    case 'ui':              return 'ui';
    case 'dead':            return 'dead';
  }
}

function sourceBadge(source: Source): { text: string; bg: string; fg: string } {
  switch (source) {
    case 'user-file':
      return { text: '🟢 USER FILE', bg: 'rgba(60,160,80,0.18)', fg: '#7ee090' };
    case 'procedural':
      return { text: '🤖 PROCEDURAL', bg: 'rgba(200,80,80,0.18)', fg: '#ff9a9a' };
    case 'shared':
      return { text: '🟠 SHARED', bg: 'rgba(220,150,40,0.18)', fg: '#ffc56b' };
    case 'missing':
      return { text: '🔴 MISSING', bg: 'rgba(220,40,40,0.22)', fg: '#ff7a7a' };
    case 'dead':
      return { text: '⚫ DEAD / UNUSED', bg: 'rgba(120,120,120,0.18)', fg: '#9aa0aa' };
  }
}

export default function SoundInspector({ onClose }: Props): React.ReactElement {
  const [filter, setFilter] = useState<Filter>('all');
  // BETA 15.73 — Category tab filter (top row). Orthogonal to the source-
  // type pill filter; both apply via AND logic.
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [checked, setChecked] = useState<Set<string>>(() => loadChecked());
  // BETA 15.70 — Per-row expansion for the Suno Prompt Library panel.
  // Only one row is expanded at a time. Click the 🎵 button to toggle.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [recentlyCopiedId, setRecentlyCopiedId] = useState<string | null>(null);

  const toggleChecked = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveChecked(next);
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const copyPrompt = (sunoId: string, slot: SoundSlot, variantIdx: number, p: SoundPrompt) => {
    const text = formatPromptForCopy(p);
    const key = `${sunoId}__${slot}__${variantIdx}`;
    void navigator.clipboard.writeText(text).then(
      () => {
        setRecentlyCopiedId(key);
        window.setTimeout(() => setRecentlyCopiedId(prev => (prev === key ? null : prev)), 1500);
      },
      () => { /* clipboard denied — silent */ },
    );
  };

  // BETA 15.66 — Quiet the background while inspector is open.
  // BETA 15.81 — Also stop Dylan's BGM track (was leaking through during
  // sample audition since it's a separate audio layer from the procedural
  // music). Restored on close if it was playing.
  useEffect(() => {
    setLabCalibratorOpen(true);
    const wasMusicPlaying = isMusicPlaying();
    if (wasMusicPlaying) stopMusic();
    const wasBgmPlaying = isBgmPlaying();
    if (wasBgmPlaying) stopBgm();
    updateAudioListener(0, 0);
    return () => {
      setLabCalibratorOpen(false);
      if (wasMusicPlaying) startMusic();
      if (wasBgmPlaying) void startBgm();
    };
  }, []);

  const filtered = useMemo(() => {
    return CATALOG.filter(e => {
      // BETA 15.73 — category tab AND source pill, both must pass.
      if (categoryFilter !== 'all' && categoryToTab(e.category) !== categoryFilter) return false;
      if (filter === 'all') return true;
      if (filter === 'unchecked') return !checked.has(e.id);
      return e.source === filter;
    });
  }, [filter, categoryFilter, checked]);

  // Group filtered entries by category, preserving CATEGORY_ORDER
  const grouped = useMemo(() => {
    const map = new Map<Category, SoundEntry[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const e of filtered) {
      map.get(e.category)?.push(e);
    }
    return map;
  }, [filtered]);

  // Counts for the summary header
  const counts = useMemo(() => {
    const c = { userFile: 0, procedural: 0, shared: 0, missing: 0, dead: 0, total: CATALOG.length };
    for (const e of CATALOG) {
      if (e.source === 'user-file') c.userFile++;
      else if (e.source === 'procedural') c.procedural++;
      else if (e.source === 'shared') c.shared++;
      else if (e.source === 'missing') c.missing++;
      else if (e.source === 'dead') c.dead++;
    }
    return c;
  }, []);

  const checkedCount = checked.size;

  // ─── Styles ───────────────────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.78)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: '"Share Tech Mono", monospace',
  };
  const panel: React.CSSProperties = {
    width: 'min(960px, 100%)',
    maxHeight: '92vh',
    background: 'rgba(8,12,18,0.98)',
    border: '1px solid rgba(80,200,255,0.35)',
    borderRadius: 4,
    color: '#cfd8e3',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
  const header: React.CSSProperties = {
    padding: '14px 20px',
    borderBottom: '1px solid rgba(80,200,255,0.18)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };
  const title: React.CSSProperties = {
    fontSize: 14,
    color: '#7fdcff',
    letterSpacing: '0.16em',
    flex: 1,
  };
  const closeBtn: React.CSSProperties = {
    background: 'rgba(40,80,120,0.5)',
    border: '1px solid rgba(80,200,255,0.4)',
    color: '#e6f2ff',
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: 11,
    letterSpacing: '0.06em',
    fontFamily: 'inherit',
    borderRadius: 2,
  };
  const filterBar: React.CSSProperties = {
    padding: '10px 20px',
    borderBottom: '1px solid rgba(80,200,255,0.12)',
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    alignItems: 'center',
    background: 'rgba(0,0,0,0.3)',
  };
  const pill = (active: boolean): React.CSSProperties => ({
    background: active ? 'rgba(80,200,255,0.25)' : 'rgba(40,60,80,0.4)',
    color: active ? '#e6f7ff' : '#9aa6b3',
    border: active ? '1px solid #7fdcff' : '1px solid rgba(80,200,255,0.2)',
    padding: '4px 10px',
    cursor: 'pointer',
    fontSize: 10,
    letterSpacing: '0.06em',
    fontFamily: 'inherit',
    borderRadius: 12,
  });
  const summary: React.CSSProperties = {
    fontSize: 10,
    color: '#7a8290',
    marginLeft: 'auto',
  };
  const body: React.CSSProperties = {
    overflowY: 'auto',
    padding: '12px 20px 20px',
    flex: 1,
  };
  const sectionH: React.CSSProperties = {
    fontSize: 11,
    color: '#7fdcff',
    letterSpacing: '0.12em',
    marginTop: 14,
    marginBottom: 6,
    paddingBottom: 4,
    borderBottom: '1px solid rgba(80,200,255,0.12)',
  };
  const row: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px dashed rgba(80,200,255,0.08)',
  };
  const playBtn: React.CSSProperties = {
    background: 'rgba(60,140,80,0.4)',
    border: '1px solid #5fcc8a',
    color: '#aaffd0',
    width: 32,
    height: 32,
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: 'inherit',
    borderRadius: 16,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const playBtnDisabled: React.CSSProperties = {
    ...playBtn,
    background: 'rgba(60,60,60,0.3)',
    border: '1px solid #555',
    color: '#666',
    cursor: 'not-allowed',
  };
  const checkBox = (isChecked: boolean): React.CSSProperties => ({
    width: 28,
    height: 28,
    cursor: 'pointer',
    border: isChecked ? '2px solid #5fcc8a' : '2px solid #4a5260',
    background: isChecked ? 'rgba(60,200,120,0.35)' : 'rgba(20,30,40,0.5)',
    color: isChecked ? '#aaffd0' : 'transparent',
    fontSize: 18,
    fontWeight: 700,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    flexShrink: 0,
    userSelect: 'none',
    transition: 'all 100ms ease',
  });
  const meta: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    color: '#e6f2ff',
    marginBottom: 2,
  };
  const fileStyle: React.CSSProperties = {
    fontSize: 10,
    color: '#9aa6b3',
    fontFamily: 'inherit',
    marginBottom: 2,
  };
  const noteStyle: React.CSSProperties = {
    fontSize: 10,
    color: '#ffb060',
    marginTop: 2,
    lineHeight: 1.4,
  };
  const traceStyle: React.CSSProperties = {
    fontSize: 9,
    color: '#5a6470',
    marginTop: 2,
  };
  const badgeStyle = (badge: { bg: string; fg: string }): React.CSSProperties => ({
    background: badge.bg,
    color: badge.fg,
    fontSize: 9,
    padding: '2px 8px',
    borderRadius: 10,
    flexShrink: 0,
    letterSpacing: '0.04em',
    height: 'fit-content',
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <div style={title}>🔊 SOUND INSPECTOR — {CATALOG.length} sounds catalogued</div>
          <span style={{ fontSize: 10, color: '#7ee090', marginRight: 8 }}>
            ✓ {checkedCount} marked done
          </span>
          <button style={closeBtn} onClick={onClose}>✕ CLOSE</button>
        </div>

        {/* BETA 15.73 — Category tabs (top row). Click to narrow to one
            category. The source-type pills (next row) apply on top via AND. */}
        <div style={{
          ...filterBar,
          borderBottom: '1px solid rgba(80,200,255,0.06)',
          gap: 4,
          paddingBottom: 8,
          paddingTop: 8,
        }}>
          {CATEGORY_TABS.map(t => {
            // Pre-compute count for the category so user sees how many entries match
            const matchCount = t.id === 'all'
              ? CATALOG.length
              : CATALOG.filter(e => categoryToTab(e.category) === t.id).length;
            return (
              <button
                key={t.id}
                style={{
                  ...pill(categoryFilter === t.id),
                  fontSize: 11,
                  fontWeight: categoryFilter === t.id ? 700 : 500,
                  borderRadius: 4,
                }}
                onClick={() => setCategoryFilter(t.id)}
                title={`${t.label} — ${matchCount} entries`}
              >
                {t.label} <span style={{ opacity: 0.6, fontSize: 9 }}>({matchCount})</span>
              </button>
            );
          })}
        </div>

        <div style={filterBar}>
          {FILTERS.map(f => (
            <button
              key={f.id}
              style={pill(filter === f.id)}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
          <span style={summary}>
            {counts.userFile} files · {counts.procedural} proc · {counts.shared} shared · {counts.missing} missing · {counts.dead} dead
          </span>
        </div>

        <div style={body}>
          <div style={{ fontSize: 10, color: '#7a8290', lineHeight: 1.5, marginBottom: 8 }}>
            Click ▶ to hear each sound the way the live game plays it (same volume, same
            spatial routing, same throttle). Click the ☐ box to mark a sound as DONE
            (you've sourced/replaced it) — stays green-checked across sessions.
          </div>
          <div style={{
            fontSize: 10, color: '#9fdaa8', lineHeight: 1.5, marginBottom: 8,
            padding: '6px 10px', background: '#0f2a1a', border: '1px solid #2a4a3a',
            borderRadius: 3,
          }}>
            🔇 Background audio paused while this is open (game frozen + music
            stopped) so you can hear each sound clearly. Restored when you close.
          </div>
          <div style={{
            fontSize: 10, color: '#ffb060', lineHeight: 1.5, marginBottom: 8,
            padding: '6px 10px', background: '#2a1f0f', border: '1px solid #4a3a1a',
            borderRadius: 3,
          }}>
            🟢 user files = real WAV/MP3 you provided. 🤖 procedural = Web Audio synth
            (no file). 🟠 shared = same file plays for multiple events. 🔴 missing =
            event currently has NO sound (strong candidate for new asset). ⚫ dead =
            file exists but no code path uses it.
          </div>

          {CATEGORY_ORDER.map(cat => {
            const entries = grouped.get(cat) ?? [];
            if (entries.length === 0) return null;
            return (
              <div key={cat}>
                <div style={sectionH}>
                  {CATEGORY_LABEL[cat]} ({entries.length})
                </div>
                {entries.map((e, idx) => {
                  const badge = sourceBadge(e.source);
                  const isChecked = checked.has(e.id);
                  const isPlayable = e.source !== 'missing' && e.source !== 'dead' || e.id === 'dead_zombie_hit';
                  const sunoId = getSunoIdFor(e.id);
                  const isExpanded = expandedId === e.id;
                  return (
                    <React.Fragment key={`${cat}-${idx}`}>
                      <div style={row}>
                        <div
                          style={checkBox(isChecked)}
                          onClick={() => toggleChecked(e.id)}
                          title={isChecked ? 'Marked done — click to uncheck' : 'Mark this sound as done (sourced / replaced)'}
                        >
                          {isChecked ? '✓' : ''}
                        </div>
                        <button
                          style={isPlayable ? playBtn : playBtnDisabled}
                          onClick={() => {
                            if (!isPlayable) return;
                            try {
                              e.play();
                            } catch (err) {
                              console.warn('[SoundInspector] play failed', err);
                            }
                          }}
                          title={isPlayable ? `Play via ${e.playFnName}()` : 'No sound available — see notes'}
                          disabled={!isPlayable}
                        >
                          ▶
                        </button>
                        <div style={meta}>
                          <div style={labelStyle}>{e.label}</div>
                          {e.file && <div style={fileStyle}>📁 {e.file}</div>}
                          {e.description && (
                            <div style={{ ...fileStyle, color: '#7a8290' }}>
                              {e.description}
                            </div>
                          )}
                          {e.fallbackFor && e.fallbackFor.length > 0 && (
                            <div style={noteStyle}>
                              ⚠ Fallback for: {e.fallbackFor.join(' · ')}
                            </div>
                          )}
                          {e.sharedWith && e.sharedWith.length > 0 && (
                            <div style={noteStyle}>
                              ⚠ Also used by: {e.sharedWith.join(' · ')}
                            </div>
                          )}
                          <div style={traceStyle}>
                            played via: {e.playFnName}
                          </div>
                        </div>
                        {sunoId && (
                          <button
                            onClick={() => toggleExpanded(e.id)}
                            title={isExpanded ? 'Hide Suno prompts' : 'Show 3 copy-able Suno prompts to generate this sound'}
                            style={{
                              background: isExpanded ? 'rgba(180,100,200,0.4)' : 'rgba(80,40,120,0.35)',
                              border: '1px solid #b067e0',
                              color: '#e0b3ff',
                              padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: 10,
                              letterSpacing: '0.04em',
                              fontFamily: 'inherit',
                              borderRadius: 4,
                              flexShrink: 0,
                              height: 'fit-content',
                              alignSelf: 'flex-start',
                            }}
                          >
                            🎵 SUNO {isExpanded ? '▾' : '▸'}
                          </button>
                        )}
                        <div style={badgeStyle(badge)}>{badge.text}</div>
                      </div>
                      {isExpanded && sunoId && (
                        <SunoPromptPanel
                          sunoId={sunoId}
                          recentlyCopiedId={recentlyCopiedId}
                          onCopy={copyPrompt}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#7a8290' }}>
              No sounds match the current filter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SunoPromptPanel — BETA 15.70
// ─────────────────────────────────────────────────────────────────────────
// Inline accordion panel showing all Suno prompt slots for a given weapon.
// Each slot has its 3 variants (Realistic / Doom-vibe / Goopzone-comedy /
// etc.) with a Copy button. Click Copy → multi-line prompt+settings goes
// to clipboard → paste in Suno → generate → save .wav.
// ─────────────────────────────────────────────────────────────────────────
function SunoPromptPanel({
  sunoId,
  recentlyCopiedId,
  onCopy,
}: {
  sunoId: string;
  recentlyCopiedId: string | null;
  onCopy: (sunoId: string, slot: SoundSlot, variantIdx: number, p: SoundPrompt) => void;
}): React.ReactElement {
  const slots = getSlotsForItem(sunoId);

  const containerStyle: React.CSSProperties = {
    background: 'rgba(40,20,60,0.4)',
    border: '1px solid rgba(176,103,224,0.35)',
    borderRadius: 4,
    margin: '4px 0 12px 42px',  // offset to align under the row's content
    padding: '12px 14px',
  };
  const headerRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: '1px dashed rgba(176,103,224,0.25)',
  };
  const headerTitle: React.CSSProperties = {
    fontSize: 12,
    color: '#e0b3ff',
    letterSpacing: '0.10em',
    flex: 1,
  };
  const openSunoBtn: React.CSSProperties = {
    background: 'rgba(120,60,180,0.4)',
    border: '1px solid #c080ff',
    color: '#f0d8ff',
    padding: '4px 10px',
    cursor: 'pointer',
    fontSize: 10,
    fontFamily: 'inherit',
    borderRadius: 4,
    textDecoration: 'none',
    letterSpacing: '0.04em',
  };
  const slotHeader: React.CSSProperties = {
    fontSize: 10,
    color: '#c8a8e0',
    letterSpacing: '0.12em',
    marginTop: 10,
    marginBottom: 6,
  };
  const variantCard: React.CSSProperties = {
    background: 'rgba(20,10,30,0.55)',
    border: '1px solid rgba(176,103,224,0.18)',
    borderRadius: 3,
    padding: '8px 10px',
    marginBottom: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };
  const variantHeaderRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
  const variantLabel: React.CSSProperties = {
    fontSize: 11,
    color: '#e0b3ff',
    fontWeight: 700,
    flex: 1,
  };
  const copyBtn = (justCopied: boolean): React.CSSProperties => ({
    background: justCopied ? 'rgba(80,200,120,0.45)' : 'rgba(80,40,120,0.5)',
    border: justCopied ? '1px solid #5fcc8a' : '1px solid #b067e0',
    color: justCopied ? '#aaffd0' : '#f0d8ff',
    padding: '3px 10px',
    cursor: 'pointer',
    fontSize: 10,
    fontFamily: 'inherit',
    borderRadius: 3,
    flexShrink: 0,
    transition: 'all 100ms ease',
  });
  const promptText: React.CSSProperties = {
    fontSize: 11,
    color: '#cfd8e3',
    lineHeight: 1.5,
    fontFamily: 'inherit',
    whiteSpace: 'pre-wrap',
  };
  const settingsLine: React.CSSProperties = {
    fontSize: 9,
    color: '#9aa6b3',
    fontFamily: 'inherit',
    letterSpacing: '0.03em',
  };

  return (
    <div style={containerStyle}>
      <div style={headerRow}>
        <div style={headerTitle}>🎵 SUNO PROMPT LIBRARY — {slots.length} slot{slots.length === 1 ? '' : 's'} × 3 variants each</div>
        <a
          style={openSunoBtn}
          href="https://suno.com/create"
          target="_blank"
          rel="noopener noreferrer"
        >↗ OPEN SUNO</a>
      </div>
      <div style={{ fontSize: 10, color: '#9aa6b3', marginBottom: 8, lineHeight: 1.4 }}>
        Click <b>COPY</b> on a variant → paste into Suno → set BPM/Key/Type per the bracketed
        settings → generate 3-5 takes → pick best → trim in Audacity → save as
        <code style={{ color: '#cfd8e3', margin: '0 2px' }}>{sunoId}_&lt;slot&gt;.wav</code>
        and drop in the next handoff to me.
      </div>

      {slots.map(slot => {
        const variants = getSoundPrompts(sunoId, slot);
        if (variants.length === 0) return null;
        return (
          <div key={slot}>
            <div style={slotHeader}>
              {SLOT_LABELS[slot]} ({variants.length} variants)
            </div>
            {variants.map((p, i) => {
              const key = `${sunoId}__${slot}__${i}`;
              const justCopied = recentlyCopiedId === key;
              return (
                <div key={i} style={variantCard}>
                  <div style={variantHeaderRow}>
                    <span style={variantLabel}>▸ {p.variantLabel}</span>
                    <button
                      style={copyBtn(justCopied)}
                      onClick={() => onCopy(sunoId, slot, i, p)}
                      title="Copy prompt + settings to clipboard"
                    >
                      {justCopied ? '✓ COPIED' : '📋 COPY'}
                    </button>
                  </div>
                  <div style={promptText}>{p.prompt}</div>
                  <div style={settingsLine}>
                    Type: <b style={{ color: '#cfd8e3' }}>{p.settings.type === 'loop' ? 'Loop' : 'One-Shot'}</b>
                    {' · '}BPM: <b style={{ color: '#cfd8e3' }}>{p.settings.bpm}</b>
                    {' · '}Key: <b style={{ color: '#cfd8e3' }}>{p.settings.key}</b>
                    {' · '}Target: <b style={{ color: '#cfd8e3' }}>{p.settings.durationHint}</b>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
